import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import Toster from "../common/Toster";
import Pagination from "../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
} from "../common/adminTable";
import { buildPaginationMeta } from "../../utils/clientPagination";
import { formatListingDateTime } from "../../utils/formatListingDate";
import { listTransactions } from "../../services/transactionsService";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../common/supplierPageMeta";
import { getTableEmptyProps } from "../common/supplierTableEmpty";
import {
  SupplierFilterToolbar,
  SupplierFilterField,
  SUPPLIER_FILTER_SELECT_CLASS,
  SupplierPageLoading,
} from "../common/supplierPageUi";

const Transactions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [status, setStatus] = useState("");
  const [serviceType, setServiceType] = useState("");

  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");
  const pageMeta = SUPPLIER_PAGE_META.transactions;

  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["transactions", currentPage, debouncedSearchTerm, status, serviceType],
    queryFn: () =>
      listTransactions(currentPage, debouncedSearchTerm, {
        status,
        service_type: serviceType,
        per_page: 10,
      }),
    keepPreviousData: true,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error) {
      setToasterMessage(error.message || "Failed to load transactions");
      setToasterType("error");
      setShowToaster(true);
    }
  }, [error]);

  const transactions = response?.data || [];
  const current_page = response?.current_page || 1;
  const last_page = response?.last_page || 1;
  const total = response?.total || 0;
  const per_page = response?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setStatus("");
    setServiceType("");
    setCurrentPage(1);
  }, []);

  const formatAmount = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatServiceType = (t) => {
    if (!t) return "—";
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  const renderStatusBadge = (s) => {
    const val = String(s || "").toLowerCase();
    if (val === "completed" || val === "success") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Completed
        </span>
      );
    }
    if (val === "failed") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Failed
        </span>
      );
    }
    if (val === "refunded") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Refunded
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pending
      </span>
    );
  };

  const hasActiveFilters = serviceType || status || searchTerm;

  return (
    <AdminPageLayout>
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={hasActiveFilters}
            onReset={clearFilters}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search transactions..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>

            <SupplierFilterField label="Service" className="md:col-span-3">
              <select
                value={serviceType}
                onChange={(e) => {
                  setServiceType(e.target.value);
                  setCurrentPage(1);
                }}
                className={SUPPLIER_FILTER_SELECT_CLASS}
              >
                <option value="">All</option>
                <option value="package">Package</option>
                <option value="event">Event</option>
                <option value="attraction">Attraction</option>
                <option value="activity">Activity</option>
                <option value="rental">Rental</option>
              </select>
            </SupplierFilterField>

            <SupplierFilterField label="Status" className="md:col-span-3">
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className={SUPPLIER_FILTER_SELECT_CLASS}
              >
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        {isLoading && !isSearching ? (
          <SupplierPageLoading message="Loading transactions..." />
        ) : (
          <AdminTable
            className="shadow-none rounded-none"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh>#</AdminTableTh>
              <AdminTableTh>Service</AdminTableTh>
              <AdminTableTh>Customer</AdminTableTh>
              <AdminTableTh>Order ID</AdminTableTh>
              <AdminTableTh>Payment</AdminTableTh>
              <AdminTableTh>Amount</AdminTableTh>
              <AdminTableTh>Status</AdminTableTh>
              <AdminTableTh>Created On</AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {transactions.length === 0 ? (
                <TableEmptyRow
                  colSpan={8}
                  {...getTableEmptyProps("transactions", {
                    hasActiveFilters: !!(searchTerm || status || serviceType),
                    onClearFilters: clearFilters,
                  })}
                />
              ) : (
                transactions.map((t, index) => (
                  <AdminTableRow key={`${t.service_type}-${t.id}`}>
                    <AdminTableCell>
                      {(current_page - 1) * per_page + index + 1}
                    </AdminTableCell>
                    <AdminTableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {t.service_name || "—"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatServiceType(t.service_type)}
                          {t.payment_type ? ` • ${t.payment_type}` : ""}
                        </p>
                      </div>
                    </AdminTableCell>
                    <AdminTableCell>{t.customer_name || "—"}</AdminTableCell>
                    <AdminTableCell nowrap className="text-xs text-gray-700">
                      {t.order_id || "—"}
                    </AdminTableCell>
                    <AdminTableCell nowrap className="text-xs text-gray-700">
                      <div>
                        <p className="text-gray-900">{t.payment_method || "—"}</p>
                        <p className="text-gray-500">{t.payment_id || ""}</p>
                      </div>
                    </AdminTableCell>
                    <AdminTableCell className="text-gray-900">
                      {formatAmount(t.amount)}
                    </AdminTableCell>
                    <AdminTableCell>{renderStatusBadge(t.status)}</AdminTableCell>
                    <AdminTableCell>
                      {formatListingDateTime(t.created_at)}
                    </AdminTableCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Transactions;
